// src/components/BlogDetail.jsx
import { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBlogById, clearCurrentBlog } from '../../redux/features/blogsSlice';
import { fetchComments } from '../../redux/features/commentsSlice';
import Navbar from '../Navbar';
import BlogHero from './BlogHero';
import CommentSection from './BlogComments';
import ErrorMessage from './../ErrorMessage';
import PageLoader from './../PageLoader';
import ShareComponent from './../ShareComponent';
import AdSlot from '../Ads/AdSlot';
import '../../styles/Blog.css';

const BlogDetail = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { currentBlog, status, error } = useSelector(state => state.blogs);
    const { items: comments, status: commentsStatus } = useSelector(state => state.comments);
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp.toDate();
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };
    const aboutRef = useRef(null);
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (id) {
                    await dispatch(fetchBlogById(id)).unwrap();
                    await dispatch(fetchComments(id)).unwrap();
                }
            } catch (error) {
                console.error('Error fetching blog or comments:', error);
            }
        };
    
        fetchData();
    
        return () => {
            dispatch(clearCurrentBlog());
        };
    }, [id, dispatch]);

    if (status === 'loading') {
        return <PageLoader />;
    }

    if (status === 'failed') {
        return <ErrorMessage message={error} />;
    }

    if (!currentBlog) {
        return <ErrorMessage message="Blog not found" />;
    }

    const scrollToAbout = () => {
        if (aboutRef.current) {
            aboutRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar scrollToAbout={scrollToAbout} />
            <BlogHero
                title={currentBlog.title || 'Untitled'}
                image={currentBlog.heroImage || 'https://via.placeholder.com/800x400'}
                author={currentBlog.author || 'Unknown'}
                date={formatDate(currentBlog.createdAt) || 'Unknown'}
            />

            <main className="max-w-4xl mx-auto px-4 py-8">
                {/* Top banner ad */}
                <div className="my-4">
                    <AdSlot type="banner" position="top" />
                </div>

                <article className="prose prose-lg mx-auto px-4 md:px-8 lg:px-0 max-w-[680px] mb-12 sm:mb-16">
                    <h1 className="text-4xl font-bold mb-6">{currentBlog.title || 'Untitled'}</h1>
                    <div
                        className="text-gray-700 [&>*]:mx-auto [&>*]:w-full
            /* HTML5 Video Handling */
            [&>video]:w-full 
            [&>video]:max-w-full 
            [&>video]:aspect-video
            [&>video]:my-8
            [&>video]:rounded-lg
            [&>video]:object-cover
            [&>video]:bg-gray-100
            
            /* Video Container Handling */
            [&>.video-container]:relative 
            [&>.video-container]:w-full 
            [&>.video-container]:pb-[56.25%] 
            [&>.video-container]:my-8
            [&>.video-container>video]:absolute 
            [&>.video-container>video]:top-0 
            [&>.video-container>video]:left-0 
            [&>.video-container>video]:w-full 
            [&>.video-container>video]:h-full 
            [&>.video-container>video]:rounded-lg
            
            /* iFrame Video Handling */
            [&>iframe]:w-full 
            [&>iframe]:max-w-full 
            [&>iframe]:aspect-video
            [&>iframe]:my-8
            
            /* Embedded Container */
            [&_.embed-container]:relative 
            [&_.embed-container]:pb-[56.25%] 
            [&_.embed-container]:h-0 
            [&_.embed-container]:overflow-hidden 
            [&_.embed-container]:max-w-full
            [&_.embed-container]:my-8
            [&_.embed-container>iframe]:absolute 
            [&_.embed-container>iframe]:top-0 
            [&_.embed-container>iframe]:left-0 
            [&_.embed-container>iframe]:w-full 
            [&_.embed-container>iframe]:h-full
            
            /* Standard Content */
            [&>p]:text-[20px] 
            [&>p]:leading-8 
            [&>p]:mb-6
            
            [&>img]:rounded-lg 
            [&>img]:w-full 
            [&>img]:max-w-full 
            [&>img]:h-auto
            [&>img]:my-8
            
            [&>h2]:text-2xl 
            [&>h2]:font-bold 
            [&>h2]:mt-12 
            [&>h2]:mb-4
            
            [&>h3]:text-xl 
            [&>h3]:font-bold 
            [&>h3]:mt-8 
            [&>h3]:mb-4
            
            [&>ul]:list-disc 
            [&>ul]:pl-6 
            [&>ul]:mb-6
            
            [&>ol]:list-decimal 
            [&>ol]:pl-6 
            [&>ol]:mb-6
            
            [&>blockquote]:border-l-4 
            [&>blockquote]:border-gray-300 
            [&>blockquote]:pl-4 
            [&>blockquote]:italic 
            [&>blockquote]:my-8
            
            [&>pre]:overflow-x-auto 
            [&>pre]:bg-gray-100 
            [&>pre]:p-4 
            [&>pre]:rounded-lg 
            [&>pre]:my-8"
                        dangerouslySetInnerHTML={{ __html: currentBlog.content }}
                    />
                    <ShareComponent
                        url={window.location.href}
                        title={currentBlog.title || 'Untitled'}
                        className="mt-12"
                    />
                </article>

                {/* In-article ad before conclusion */}
                <div className="my-8">
                    <AdSlot type="in-feed" position="bottom" />
                </div>

                {/* Comments Section */}
                <CommentSection
                    blogId={id}
                    comments={comments}
                    status={commentsStatus}
                />
            </main>

            {/* Bottom banner ad */}
            <div className="my-8">
                <AdSlot type="banner" position="bottom" />
            </div>
        </div>
    );
};

export default BlogDetail;